import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {FieldInfoConfiguration} from "./field-info-configuration";

/**
 * Content elements define what should display within the
 * [PopupTemplate](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html) content.
 * There are five specific element types:
 * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-Content.html)
 */
@JsonObject()
export class ContentConfiguration {
    /**
     * The type of popup element displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-Content.html#type)
     */
    @JsonProperty()
    declare type: "text" | "fields" | "media" | "attachments" | "custom" | "expression" | "relationship";

    constructor() {}
}

@JsonObject()
export class CustomContentConfiguration extends ContentConfiguration {
    /**
     * The function that is called to create the custom content element.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-CustomContent.html#creator)
     */
    // creator?: | PopupTemplateContentCreator
    //     | string
    //     | HTMLElement
    //     | WidgetProperties
    //     | Promise<string | HTMLElement | WidgetProperties>;
    /**
     * The called function to cleanup custom content when it is no longer necessary.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-CustomContent.html#destroyer)
     */
    // declare destroyer: PopupTemplateContentDestroyer;
    /**
     * An array of field names used in the [PopupTemplate](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-CustomContent.html#outFields)
     */
    @JsonProperty()
    declare outFields: string[];

    constructor() {
        super();
    }
}

@JsonObject()
export class FieldsContentConfiguration extends ContentConfiguration {
    /**
     * Array of [fieldInfos](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-FieldInfo.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-FieldsContent.html#fieldInfos)
     */
    @JsonProperty()
    declare fieldInfos: FieldInfoConfiguration[];

    constructor() {
        super();
    }
}

@JsonObject()
export class MediaContentConfiguration extends ContentConfiguration {
    /**
     * Index of the current active media within the popup's media content.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-MediaContent.html#activeMediaInfoIndex)
     */
    @JsonProperty()
    declare activeMediaInfoIndex?: number;
    /**
     * Describes the media's content in detail.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-MediaContent.html#description)
     */
    @JsonProperty()
    declare description?: string;
    /**
     * Contains the media elements representing images or charts to display within the [PopupTemplate]
     * (https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-MediaContent.html#mediaInfos)
     */
    // @JsonProperty()
    // declare mediaInfos:
    //   | BarChartMediaInfo
    //    | ColumnChartMediaInfo
    //    | ImageMediaInfo
    //    | LineChartMediaInfo
    //    | PieChartMediaInfo
    //    | any[];
    /**
     * Heading indicating what the media's content represents.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-MediaContent.html#title)
     */
    @JsonProperty()
    declare title?: string;
    constructor() {
        super();
    }
}

@JsonObject()
export class TextContentConfiguration extends ContentConfiguration {
    /**
     * The formatted string content to display.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-TextContent.html#text)
     */
    @JsonProperty()
    declare text: string;

    constructor() {
        super();
    }
}

@JsonObject()
export class AttachmentsContentConfiguration extends ContentConfiguration {
    /**
     * Describes the attachment's content in detail.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-AttachmentsContent.html#description)
     */
    @JsonProperty()
    declare description?: string;
    /**
     * A string value indicating how to display an attachment.
     *
     * @default "auto"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-AttachmentsContent.html#displayType)
     */
    @JsonProperty()
    declare displayType: "auto" | "preview" | "list";
    /**
     * A heading indicating what the attachment's content represents.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-AttachmentsContent.html#title)
     */
    @JsonProperty()
    declare title?: string;

    constructor() {
        super();
    }
}
@JsonObject()
export class ElementExpressionInfoConfiguration {
    /**
     * The [Arcade](https://developers.arcgis.com/javascript/latest/arcade/) expression evaluating to a dictionary.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-ElementExpressionInfo.html#expression)
     */
    @JsonProperty()
    declare expression?: string;
    /**
     * The return type of the expression.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-ElementExpressionInfo.html#returnType)
     */
    returnType?: "dictionary";
    /**
     * The title used to describe the popup element returned by the expression.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-ElementExpressionInfo.html#title)
     */
    @JsonProperty()
    declare title?: string;
}
@JsonObject()
export class ExpressionContentConfiguration extends ContentConfiguration {
    /**
     * Contains the Arcade expression used to create a popup content element.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-ExpressionContent.html#expressionInfo)
     */
    @JsonProperty()
    declare expressionInfo?: ElementExpressionInfoConfiguration;

    constructor() {
        super();
    }
}
@JsonObject()
export class RelatedRecordsInfoFieldOrderProperties {
    /**
     * The attribute value of the field selected that will drive the sorting of related records.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-support-RelatedRecordsInfoFieldOrder.html#field)
     */
    @JsonProperty()
    declare field?: string;
    /**
     * Set the ascending or descending sort order of the returned related records.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-support-RelatedRecordsInfoFieldOrder.html#order)
     */
    @JsonProperty()
    declare order?: "asc" | "desc";
}
@JsonObject()
export class RelationshipContentConfiguration extends ContentConfiguration {
    /**
     * Describes the relationship's content in detail.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-RelationshipContent.html#description)
     */
    @JsonProperty()
    declare description?: string;
    /**
     * A numeric value indicating the maximum number of related features to display in the list of related records.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-RelationshipContent.html#displayCount)
     */
    @JsonProperty()
    declare displayCount?: number;
    /**
     * A string value indicating how to display related records within the relationship content.
     *
     * @default "list"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-RelationshipContent.html#displayType)
     */
    @JsonProperty()
    declare displayType?: "list";
    /**
     * An array of [RelatedRecordsInfoFieldOrder](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-support-RelatedRecordsInfoFieldOrder.html) indicating the display order for the related records, and whether they should be sorted in ascending `asc` or descending `desc` order.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-RelationshipContent.html#orderByFields)
     */
    @JsonProperty()
    declare orderByFields?: RelatedRecordsInfoFieldOrderProperties[];
    /**
     * The numeric id value for the defined relationship.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-RelationshipContent.html#relationshipId)
     */
    @JsonProperty()
    declare relationshipId?: number;
    /**
     * A heading indicating what the relationship's content represents.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-content-RelationshipContent.html#title)
     */
    @JsonProperty()
    declare title?: string;

    constructor() {
        super();
    }
}
@JsonObject()
export class CustomChartXYConfiguration {
    @JsonProperty()
    declare labels?: Map<number, string>;
    @JsonProperty()
    declare min?: number;
    @JsonProperty()
    declare max?: number;
}
@JsonObject()
export class CustomChartAxisConfiguration {
    @JsonProperty()
    declare x?: CustomChartXYConfiguration;
    @JsonProperty()
    declare y?: CustomChartXYConfiguration;
}
@JsonObject()
export class CustomChartTooltipConfiguration {
    @JsonProperty()
    declare content: string;
    @JsonProperty()
    declare specialContent?: string[];
}
@JsonObject()
export class CustomChartConfiguration extends CustomContentConfiguration {
    @JsonProperty()
    declare labels: string[];
    @JsonProperty()
    declare axis: CustomChartAxisConfiguration;
    @JsonProperty()
    declare tooltip?: CustomChartTooltipConfiguration;
    @JsonProperty()
    declare mediaInfos: __esri.MediaInfo[];

    constructor() {
        super();
    }
}